import { Play, Sparkles, Heart } from 'lucide-react';
import type { DayPlan } from '../../types/exercise';

interface HeroCardProps {
  plan: DayPlan;
  isToday: boolean;
  completedExercises: number;
  totalExercises: number;
  onStart?: (() => void) | undefined;
}

export function HeroCard({
  plan,
  isToday,
  completedExercises,
  totalExercises,
  onStart,
}: HeroCardProps): JSX.Element {
  const eyebrow = isToday ? 'Heute' : 'Geplanter Tag';

  if (plan.kind === 'pilates') {
    return (
      <article className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 md:p-8">
        <p className="text-xs font-semibold uppercase tracking-wider text-amber-700">
          {eyebrow}
        </p>
        <div className="mt-2 flex items-start gap-3">
          <Sparkles aria-hidden="true" className="size-7 shrink-0 text-amber-500" />
          <div>
            <h2 className="text-2xl font-semibold leading-tight text-zinc-900 md:text-3xl">
              {plan.title}
            </h2>
            <p className="mt-2 text-base leading-normal text-zinc-700">{plan.body}</p>
          </div>
        </div>
      </article>
    );
  }

  if (plan.kind === 'recovery') {
    return (
      <article className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-6 md:p-8">
        <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">
          {eyebrow}
        </p>
        <div className="mt-2 flex items-start gap-3">
          <Heart aria-hidden="true" className="size-7 shrink-0 text-emerald-500" />
          <div>
            <h2 className="text-2xl font-semibold leading-tight text-zinc-900 md:text-3xl">
              {plan.title}
            </h2>
            <p className="mt-2 text-base leading-normal text-zinc-700">
              Stretching, lockerer Spaziergang, kein Bandtraining.
            </p>
          </div>
        </div>
      </article>
    );
  }

  // training
  const isComplete = totalExercises > 0 && completedExercises >= totalExercises;
  const ratio = totalExercises > 0 ? completedExercises / totalExercises : 0;
  const fillPct = Math.round(ratio * 100);

  return (
    <article className="overflow-hidden rounded-2xl border border-zinc-200 bg-gradient-to-br from-emerald-600 to-emerald-700 text-white shadow-sm">
      <div className="px-6 pt-6 pb-4 md:px-8 md:pt-8">
        <p className="text-xs font-semibold uppercase tracking-wider text-emerald-100">
          {eyebrow}
        </p>
        <h2 className="mt-2 text-2xl font-semibold leading-tight md:text-3xl">{plan.title}</h2>
        {plan.warmup ? (
          <div className="mt-2 text-sm leading-normal text-emerald-50/90">
            <p className="font-semibold">Warmup</p>
            <p className="whitespace-pre-line">{plan.warmup}</p>
          </div>
        ) : null}

        <div className="mt-4 flex items-baseline gap-3">
          <span className="text-3xl font-semibold tabular-nums">
            {completedExercises}
            <span className="text-base font-normal text-emerald-100">/{totalExercises}</span>
          </span>
          <span className="text-sm text-emerald-100">Uebungen abgeschlossen</span>
        </div>

        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-emerald-800/50">
          <div
            className="h-full rounded-full bg-emerald-300 transition-[width] duration-300"
            style={{ width: `${fillPct}%` }}
          />
        </div>
      </div>

      {onStart ? (
        <div className="border-t border-emerald-500/40 bg-emerald-700/40 px-6 py-3 md:px-8">
          <button
            type="button"
            onClick={onStart}
            className="inline-flex h-11 items-center gap-2 rounded-lg bg-white px-4 text-sm font-semibold text-emerald-700 transition-transform duration-[120ms] active:scale-[0.98] active:bg-emerald-50 focus-visible:outline-2 focus-visible:outline-white focus-visible:outline-offset-2"
          >
            <Play aria-hidden="true" className="size-4" />
            {isComplete ? 'Uebungen ansehen' : 'Training starten'}
          </button>
        </div>
      ) : null}
    </article>
  );
}
